import { GradientParser } from '../../lib/gradient';
import { loadImage } from './image';
import { extractShaderColors, isShaderGradientFill, renderShaderFill } from './shader-fill';
import { isGradientPatternFill, createGradientPattern } from './gradient-pattern';
import { loadImageCache } from '../image';
import { getNodeImageCache } from '../../provider';

// "url(\"https://www.transparenttextures.com/patterns/old-map.png\")"
// Also supports data URIs like: url("data:image/svg+xml,%3Csvg...")
export function isURLFill(fill) {
  if (typeof fill !== 'string') return false;
  // Match CSS url() pattern with optional quotes and whitespace
  // Use a more permissive regex that handles complex URLs including data URIs with special characters
  const trimmed = fill.trim();
  if (!trimmed.startsWith('url(') || !trimmed.endsWith(')')) return false;
  return true;
}

// "url(\"https://www.transparenttextures.com/patterns/old-map.png\")"
// Also supports data URIs like: url("data:image/svg+xml,%3Csvg...")
// Supports web background-image url() syntax including:
// - url(path/to/image.png)
// - url("path/to/image.png")
// - url('path/to/image.png')
// - url("data:image/svg+xml,...")
// - url("data:image/png;base64,...")
export function parseURLFill(fill) {
  if (!isURLFill(fill)) return null;

  const trimmed = fill.trim();
  // Remove 'url(' prefix and ')' suffix
  let inner = trimmed.slice(4, -1).trim();

  // Handle quoted URLs (single or double quotes)
  if (
    (inner.startsWith('"') && inner.endsWith('"')) ||
    (inner.startsWith("'") && inner.endsWith("'"))
  ) {
    inner = inner.slice(1, -1);
  }

  // Handle escaped characters in URLs
  // CSS allows escaping quotes and other characters with backslash
  inner = inner.replace(/\\(.)/g, '$1');

  return inner || null;
}

export function createFill(renderContext, fill, node, render) {
  const { context } = renderContext;
  try {
    render =
      render ||
      (() => {
        context.fill();
      });
    if (isShaderGradientFill(fill)) {
      const handled = renderShaderFill(renderContext, fill, node);
      if (handled) return;
      // graceful fallback using provided colors if any
      const colors = extractShaderColors(fill);
      if (colors.length >= 2) {
        const stops = colors.map((c, i) => `${c} ${(i / (colors.length - 1)) * 100}%`).join(', ');
        const gradient = `linear-gradient(90deg, ${stops})`;
        GradientParser.create(
          context,
          gradient,
          {
            x: node.x,
            y: node.y,
            w: Math.abs(node.w),
            h: Math.abs(node.h),
          },
          render
        );
        return;
      } else if (colors.length === 1) {
        context.fillStyle = colors[0];
        render();
        return;
      }
      // if not handled, fallthrough to other types for graceful degrade
    }
    // Handle gradient pattern (gradient + background-size for tiled patterns)
    if (isGradientPatternFill(fill)) {
      const pattern = createGradientPattern(context, fill, { w: node.w, h: node.h });
      if (pattern) {
        context.fillStyle = pattern;
        render();
      }
      return;
    }
    if (GradientParser.is(fill)) {
      GradientParser.create(
        context,
        fill,
        {
          x: node.x,
          y: node.y,
          w: Math.abs(node.w),
          h: Math.abs(node.h),
        },
        render
      );
    } else if (isURLFill(fill)) {
      const url = parseURLFill(fill);
      if (url) {
        loadImageCache(renderContext, node);
        const imgCache = getNodeImageCache(renderContext, node.id);
        // Wait for image to load before creating pattern
        if (!imgCache.img || imgCache.loading) {
          // Image still loading, will re-render when ready via markNeedRender
          return;
        }

        // Cache pattern to avoid recreating on every render
        if (!imgCache.pattern) {
          imgCache.pattern = context.createPattern(imgCache.img, 'repeat');
        }

        if (imgCache.pattern) {
          context.fillStyle = imgCache.pattern;
          render();
        }
      } else {
        context.fillStyle = '#ffffff'; // Fallback if URL is invalid
        render();
      }
    } else {
      if (fill !== 'none') {
        context.fillStyle = fill || '#ffffff';
        render();
      }
    }
  } catch (e) {
    // console.error(e);
  }
}
